import { CaretDownOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deviceAdminApi } from '../../../../api/DeviceAdminApi';
import {
	buildColumn,
	ColumnsProps,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	ScrollType,
	useHandleOnChangeTableLogic,
} from '../../../../Helper';
import { getPermissionErrorMessage, User } from '../../../../model/AccountModel';
import { BaseColumns, OperatorsColumn, PaginationSetting, SelectedDeviceKeys, SelectOptions } from '../../../../model/CommonModel';
import { DeviceObjectType, OperatorModel, OptionsButtonBuilder } from '../../../../model/DeviceAdminModel';
import { useStoreSelector } from '../../../../store';
import { setLoading } from '../../../../store/common/actions';
import {
	clearVelocityConfigurationSelectionsAction,
	setOperatorsBy,
	setOperatorsByData,
	setVelocityConfigurationFilterMode,
} from '../../../../store/deviceControl/actions';
import { selectOperators, selectTablePaginationSetting } from '../../../../store/deviceControl/selectors';
import { ButtonDropdown, ModalConfirmationList, SearchColumn } from '../../../common';
import { EditableCell } from '../../../common/EditableCell/EditableCell';
import styles from './operators.module.scss';

type Props = {};

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const isUserAdmin: boolean = user.isAdmin;
const scroll: ScrollType = { x: 379, y: 430 };
const maxLength: number = 20;
const deviceObjectType: DeviceObjectType = DeviceObjectType.Operators;

const createButtonOptions = (): OptionsButtonBuilder<string> => {
	return {
		labelOrIcon: _('Action'),
		options: [
			{
				label: _('Enable'),
				disabled: !isUserAdmin,
				value: 'enableOperator',
				title: getPermissionErrorMessage(isUserAdmin),
			},
			{
				label: _('LockDown'),
				disabled: !isUserAdmin,
				value: 'lockDownOperator',
				title: getPermissionErrorMessage(isUserAdmin),
			},
		],
	};
};

const Operators: React.FC<Props> = () => {
	const operators: OperatorModel[] = useStoreSelector(selectOperators);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);

	const dispatch = useDispatch();
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [tableData, setTableData] = useState<OperatorsColumn[]>([]);

	const isSearchPerformed = useRef<boolean>(false);

	const clearSelection = () => {
		dispatch(clearVelocityConfigurationSelectionsAction({ objectType: deviceObjectType }));
		setSelectedRowKeys([]);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	useEffect(() => {
		const mapRes: OperatorsColumn[] = createData();
		setTableData(mapRes);
	}, [JSON.stringify(operators)]);

	useEffect(() => {
		return () => {
			clearSelection();
		};
	}, []);

	const onEnableRequest = async (selectedOperators: OperatorModel[]): Promise<void> => {
		dispatch(setLoading(true));

		for (const operator of selectedOperators) {
			const response = await deviceAdminApi.enableOperator(operator);
			handleResponse(response);
		}
	};

	const onLockDownRequest = async (selectedOperators: OperatorModel[]): Promise<void> => {
		dispatch(setLoading(true));

		for (const operator of selectedOperators) {
			const response = await deviceAdminApi.lockDownOperator(operator);
			handleResponse(response);
		}
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const disabled: boolean = false;
	const items = createButtonOptions();

	const handleOnSearchResults = (searchedValue: string) => {
		dispatch(setOperatorsBy({ ...tablePaginationSetting, TotalItems: 0, PageNumber: 1, SearchedValue: searchedValue }));
	};

	const columns: ColumnsProps<OperatorsColumn>[] = [
		{
			...buildColumn(_('Name'), 'Name', 'auto', 'start'),
			sorter: (a, b) => a.Name.localeCompare(b.Name),
			...SearchColumn({
				maxLength,
				dataIndex: 'Name',
				reset: undefined,
				label: undefined,
				notVisible: undefined,
				resetSearch: shouldResetSearchColumn,
				setIsFilterMode: setVelocityConfigurationFilterMode,
				clearSelection,
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearchResults(searchedValue),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			}),
		},
		{
			...buildColumn(_('Status'), 'OperatorStatus', 'auto', 'start'),
			sorter: (a, b) => a.OperatorStatus.localeCompare(b.OperatorStatus),
		},
	];

	const onChangeSelection = (key: BaseColumns) => {
		const operatorId = key.key.toString();
		if (selectedRowKeys.findIndex(key => key === operatorId) === -1) {
			const cloneState: OperatorModel[] = operators.map(x => (x.Id.toString() === operatorId ? { ...x, checked: true } : { ...x, checked: false }));
			dispatch(setOperatorsByData(cloneState));
			setSelectedRowKeys([operatorId]);
		}
	};

	const isEditMode: boolean = false;
	const contextMenuOptions: SelectOptions<string>[] = [...items?.options];

	const mergedColumns = columns.map(col => ({
		...col,
		onCell: (record: OperatorsColumn) => ({
			record,
			dataIndex: col.dataIndex,
			title: col.title,
			maxLength,
			options: contextMenuOptions,
			isTableInEditMode: isEditMode,
			onChangeSelection,
			onClickOption: handleActionScope,
		}),
	}));

	const getSelectedOperators = async (): Promise<SelectedDeviceKeys[]> => {
		const response = await deviceAdminApi.getVelocityDevicesBySelectedKeys(deviceObjectType, selectedRowKeys as string[]);

		if (handleResponse(response)) {
			return [];
		}

		return response.Entity;
	};

	const handleActionScope = async (key: string) => {
		const selectedOperatorsKeys = await getSelectedOperators();
		const selectedOperators = selectedOperatorsKeys.map(x => ({ Id: x.Id, Name: x.Name } as OperatorModel));
		switch (key) {
			case 'enableOperator': {
				const operatorAction: string = _('Enable');
				ModalConfirmationList({
					type: operatorAction,
					translationKey: 'AreYouSureYouWantToTheseOperators',
					data: selectedOperators,
					onConfirm: () =>
						onEnableRequest(selectedOperators).then(res => {
							dispatch(setOperatorsBy(tablePaginationSetting));
						}),
				});
				break;
			}

			case 'lockDownOperator': {
				const operatorAction: string = _('LockDown');
				ModalConfirmationList({
					type: operatorAction,
					translationKey: 'AreYouSureYouWantToTheseOperators',
					data: selectedOperators,
					onConfirm: () =>
						onLockDownRequest(selectedOperators).then(res => {
							dispatch(setOperatorsBy(tablePaginationSetting));
						}),
				});
				break;
			}
		}
	};

	const getSelectedRowKeysForSelectAll = async (newSelectedRowKeys: React.Key[]): Promise<React.Key[]> => {
		const response = await deviceAdminApi.getOperators();
		const commandSetsIds = response?.map<string>(x => x.Id.toString());
		return getUniqueValuesArray(newSelectedRowKeys, commandSetsIds);
	};

	const handleChange = (selectedRowKeys: React.Key[]) => {
		const cloneState: OperatorModel[] = operators.map(x => ({ ...x, checked: selectedRowKeys.findIndex(r => r === x.Id) !== -1 }));
		dispatch(setOperatorsByData(cloneState));
		setSelectedRowKeys(selectedRowKeys);
	};

	const handleSelectAll = async () => {
		setIsTableLoading(true);
		const newSelectedRowKeys = await getSelectedRowKeysForSelectAll(selectedRowKeys);

		const cloneState: OperatorModel[] = operators.map(x => ({ ...x, checked: newSelectedRowKeys.findIndex(r => r === x.Id?.toString()) !== -1 }));

		dispatch(setOperatorsByData(cloneState));
		setSelectedRowKeys(newSelectedRowKeys);
		setIsTableLoading(false);
	};

	const handleSelectInvert = () => {
		const dataKeys = operators.map<React.Key>(x => x.Id?.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		const cloneState: OperatorModel[] = operators.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.Id?.toString()) !== -1,
		}));

		dispatch(setOperatorsByData(cloneState));
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const createActionElement = (operator: OperatorModel): React.ReactNode => {
		const content: React.ReactNode = undefined;

		return <div className={styles.actions}>{content}</div>;
	};

	const createData = (): OperatorsColumn[] => {
		if (operators.length > 0) {
			return operators.map<OperatorsColumn>((data: OperatorModel) => ({
				key: data.Id.toString(),
				Action: createActionElement(data),
				Name: data.Name,
				OperatorStatus: data.OperatorStatus ? _('Enabled') : _('LockedDown'),
			}));
		}

		return [];
	};

	const rowSelection: TableRowSelection<OperatorsColumn> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => ({
			id: `operatorsTableCheckbox-${record.key}`,
			disabled,
			children: <label htmlFor={`operatorsTableCheckbox-${record.key}`} className={styles.srOnly}>{`${_('SelectOperator')}`}</label>,
		}),
		selectedRowKeys,
		onChange: handleChange,
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<OperatorsColumn>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			dispatch(
				setOperatorsBy({
					...tablePaginationSetting,
					PageNumber: current,
					PageSize: pageSize,
					SortDirection: sortOrder,
					SortField: sortField.toString(),
				})
			);
		}
	};

	const disabledActionButtons = disabled || selectedRowKeys.length === 0;

	return (
		<div className={styles.container}>
			<div className={styles.buttonContainer}>
				<ButtonDropdown
					id={'operatorsTableActionDropdown'}
					disabled={disabledActionButtons}
					menuOptions={items.options}
					icon={<CaretDownOutlined />}
					labelIcon={items.labelOrIcon}
					onClickOption={handleActionScope}
				/>
			</div>
			<Table
				id='operatorsTable'
				columns={mergedColumns as ColumnsType}
				components={{
					body: {
						cell: EditableCell,
					},
				}}
				dataSource={tableData}
				pagination={pagination}
				scroll={scroll}
				size='small'
				className={styles.operatorsTable}
				rowSelection={rowSelection}
				onChange={handleOnChangeTable}
				loading={isTableLoading}
			/>
		</div>
	);
};

export { Operators };
